<script lang="ts">
  // Mandatory sign-on gate: the user must scroll through and accept the NDA /
  // HIPAA / product-protection agreement before the app is usable. Acceptance is
  // recorded in Firestore (append-only). Shown only when not yet accepted.
  import { user, acceptAgreement } from '../lib/stores';
  import { AGREEMENT_TITLE, AGREEMENT_COMPANY, AGREEMENT_EFFECTIVE, AGREEMENT_VERSION, AGREEMENT_SECTIONS } from '../lib/agreement';
  import { signOutUser } from '../lib/firebase';

  let checked = $state(false);
  let scrolledEnd = $state(false);
  let busy = $state(false);
  let err = $state('');

  function onScroll(e: Event) {
    const el = e.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) scrolledEnd = true;
  }
  async function accept() {
    if (!checked || !scrolledEnd || busy) return;
    busy = true; err = '';
    try { await acceptAgreement(); }
    catch (e) { err = (e as Error)?.message || 'Could not record acceptance. Try again.'; busy = false; }
  }
  async function decline() { try { await signOutUser(); } catch (_) {} }
</script>

<div class="wrap">
  <div class="card" role="dialog" aria-modal="true" aria-label={AGREEMENT_TITLE}>
    <header class="head">
      <div class="badge">Required</div>
      <h1>{AGREEMENT_TITLE}</h1>
      <p class="sub faint">{AGREEMENT_COMPANY} · Effective {AGREEMENT_EFFECTIVE} · Version {AGREEMENT_VERSION}</p>
      <p class="sub faint">Signing in as {$user?.email ?? 'your account'}. You must read and accept this agreement to continue.</p>
    </header>

    <div class="body" onscroll={onScroll}>
      {#each AGREEMENT_SECTIONS as s (s.h)}
        <section>
          <h2>{s.h}</h2>
          {#each s.p as para (para)}<p>{para}</p>{/each}
        </section>
      {/each}
      <p class="endmark faint">— End of Agreement —</p>
    </div>

    <footer class="foot">
      {#if !scrolledEnd}<p class="hint faint">Scroll to the end of the agreement to enable acceptance.</p>{/if}
      <label class="agree" class:disabled={!scrolledEnd}>
        <input type="checkbox" bind:checked disabled={!scrolledEnd} />
        <span>I have read, understood, and agree to be bound by this Agreement, including its confidentiality, HIPAA, and software &amp; product protection terms. I understand my acceptance is recorded as a binding electronic signature.</span>
      </label>
      {#if err}<p class="err">{err}</p>{/if}
      <div class="acts">
        <button class="decline" onclick={decline} disabled={busy}>Decline &amp; sign out</button>
        <button class="accept" onclick={accept} disabled={!checked || !scrolledEnd || busy}>{busy ? 'Recording…' : 'I Agree'}</button>
      </div>
    </footer>
  </div>
</div>

<style>
  .wrap { position: fixed; inset: 0; z-index: var(--z-sheet, 200); display: grid; place-items: center; padding: var(--space-4); background: var(--bg); }
  .card { display: flex; flex-direction: column; width: min(100%, 720px); max-height: min(92dvh, 900px); background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-lg); box-shadow: 0 24px 70px -20px color-mix(in srgb, var(--bg) 92%, black); overflow: hidden; }
  .head { padding: var(--space-5) var(--space-5) var(--space-4); border-bottom: 1px solid var(--line); }
  .badge { display: inline-block; font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--on-accent); background: var(--accent-grad); border-radius: var(--radius-pill); padding: 2px var(--space-3); margin-bottom: var(--space-2); }
  h1 { font-size: var(--fs-lg); font-weight: 800; line-height: 1.2; }
  .sub { font-size: var(--fs-2xs); margin-top: 4px; }
  .body { flex: 1; min-height: 0; overflow-y: auto; padding: var(--space-4) var(--space-5); display: flex; flex-direction: column; gap: var(--space-3); }
  .body section { display: flex; flex-direction: column; gap: var(--space-1); }
  .body h2 { font-size: var(--fs-sm); font-weight: 700; color: var(--text); }
  .body p { font-size: var(--fs-xs); line-height: 1.65; color: var(--text-2); }
  .endmark { text-align: center; font-size: var(--fs-2xs); padding: var(--space-2) 0; }
  .foot { border-top: 1px solid var(--line); padding: var(--space-4) var(--space-5); display: flex; flex-direction: column; gap: var(--space-3); background: var(--surface-2); }
  .hint { font-size: var(--fs-2xs); }
  .agree { display: flex; align-items: flex-start; gap: var(--space-2); font-size: var(--fs-xs); line-height: 1.5; color: var(--text); cursor: pointer; }
  .agree.disabled { opacity: 0.55; cursor: default; }
  .agree input { margin-top: 2px; width: 16px; height: 16px; flex: 0 0 auto; accent-color: var(--accent); }
  .err { font-size: var(--fs-xs); color: var(--danger); }
  .acts { display: flex; justify-content: space-between; gap: var(--space-2); }
  .decline { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); color: var(--text-2); border: 1px solid var(--line); font-size: var(--fs-xs); font-weight: 700; }
  .decline:hover:not(:disabled) { color: var(--text); border-color: var(--line-strong); }
  .accept { padding: var(--space-2) var(--space-6); border-radius: var(--radius-pill); background: var(--accent-grad); color: var(--on-accent); font-size: var(--fs-sm); font-weight: 800; }
  .accept:disabled { opacity: 0.5; }
  @media (max-width: 600px) { .head, .body, .foot { padding-left: var(--space-4); padding-right: var(--space-4); } }
</style>
